var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// SPDX-FileCopyrightText: 2023 Eduardo Robles <edu@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import React, { useContext } from "react";
import Keycloak from "keycloak-js";
import { createContext, useEffect, useState } from "react";
import { isNull, isString, sleep } from "@sequentech/ui-core";
import { IPermissions } from "@/types/keycloak";
import { SettingsContext } from "./SettingsContextProvider";
import { useLocation, useNavigate } from "react-router-dom";
import SelectTenant from "@/screens/SelectTenant";
import { Dialog } from "@sequentech/ui-essentials";
import { useTranslation } from "react-i18next";
/**
 * Default values for the {@link AuthContext}
 */
var defaultAuthContextValues = {
    isAuthenticated: false,
    userId: "",
    username: "",
    email: "",
    firstName: "",
    tenantId: "",
    trustee: "",
    logout: function () { },
    hasRole: function () { return false; },
    getAccessToken: function () { return undefined; },
    isAuthorized: function () { return false; },
    openProfileLink: function () { return new Promise(function () { return undefined; }); },
    permissionLabels: [],
    updateTokenAndPermissionLabels: function () { },
    isGoldUser: function () { return false; },
    reauthWithGold: function () { return __awaiter(void 0, void 0, void 0, function () { return __generator(this, function (_a) {
        return [2 /*return*/];
    }); }); },
    initKeycloak: function (tenantId) { return __awaiter(void 0, void 0, void 0, function () {
        return __generator(this, function (_a) {
            return [2 /*return*/, false];
        });
    }); },
};
/**
 * Create the AuthContext using the default values.
 */
export var AuthContext = createContext(defaultAuthContextValues);
var generateTokenStorage = function (newKeycloak) {
    var _a, _b, _c;
    var nowInSeconds = Math.floor(Date.now() / 1000);
    var token = [(_a = newKeycloak.token) !== null && _a !== void 0 ? _a : "", (_c = (_b = newKeycloak.refreshTokenParsed) === null || _b === void 0 ? void 0 : _b.exp) !== null && _c !== void 0 ? _c : nowInSeconds].join(":");
    return token;
};
var readTokenStorage = function () {
    var token = localStorage.getItem("token");
    if (!token) {
        return null;
    }
    var _a = token.split(":"), tokenStr = _a[0], expStr = _a[1];
    var exp = parseInt(expStr);
    var nowInSeconds = Math.floor(Date.now() / 1000);
    if (exp < nowInSeconds) {
        return null;
    }
    return tokenStr;
};
/**
 * AuthContextProvider is responsible for managing the authentication state of the current user.
 *
 * @param props
 */
var AuthContextProvider = function (props) {
    var _a = useContext(SettingsContext), loadedGlobalSettings = _a.loaded, globalSettings = _a.globalSettings;
    var _b = useState(null), keycloak = _b[0], setKeycloak = _b[1];
    var _c = useState(false), isKeycloakInitialized = _c[0], setIsKeycloakInitialized = _c[1];
    // Create the local state in which we will keep track if a user is authenticated
    var _d = useState(false), isAuthenticated = _d[0], setAuthenticated = _d[1];
    // Local state that will contain the users name once it is loaded
    var _e = useState(""), userId = _e[0], setUserId = _e[1];
    var _f = useState(""), username = _f[0], setUsername = _f[1];
    var _g = useState(""), email = _g[0], setEmail = _g[1];
    var _h = useState(""), firstName = _h[0], setFirstName = _h[1];
    var _j = useState(""), tenantId = _j[0], setTenantId = _j[1];
    var _k = useState(""), trustee = _k[0], setTrustee = _k[1];
    var _l = useState([]), permissionLabels = _l[0], setPermissionLabels = _l[1];
    var _m = useState(localStorage.getItem("selected-tenant-id")), selectedTenantId = _m[0], setSelectTenantId = _m[1];
    var modifySelectedTenantId = function (val) {
        if (null === val) {
            localStorage.removeItem("selected-tenant-id");
        }
        else {
            localStorage.setItem("selected-tenant-id", val);
        }
        setSelectTenantId(val);
    };
    var _o = React.useState(false), openModal = _o[0], setOpenModal = _o[1];
    var _p = useTranslation(), t = _p.t, i18n = _p.i18n;
    var sleepSecs = 50;
    var bufferSecs = 10;
    var navigate = useNavigate();
    var location = useLocation();
    var fetchGraphQL = function (operationsDoc, operationName, variables) { return __awaiter(void 0, void 0, void 0, function () {
        var result;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, fetch(globalSettings.HASURA_URL, {
                        method: "POST",
                        body: JSON.stringify({
                            query: operationsDoc,
                            variables: variables,
                        }),
                    })];
                case 1:
                    result = _a.sent();
                    return [2 /*return*/, result.json()];
            }
        });
    }); };
    var operation = "\n        query GetAllTenants {\n            sequent_backend_tenant {\n                id\n                slug\n            }\n        }\n    ";
    useEffect(function () {
        if (location.pathname.endsWith("/tenant")) {
            if (readTokenStorage()) {
                setOpenModal(true);
            }
            else {
                setOpenModal(false);
                modifySelectedTenantId(null);
            }
        }
    }, [location.pathname.endsWith("/tenant"), localStorage.getItem("token"), selectedTenantId]);
    /**
     * Initializes the Keycloak instance for the specified tenant and handles authentication.
     *
     * @param {string} tenantId - The ID of the tenant to initialize Keycloak for.
     * @returns {Promise<boolean>} - A promise that resolves to `true` if the user is authenticated, `false` otherwise.
     *
     * @throws {Error} - Throws an error if the initialization process fails.
     *
     * @remarks
     * This function performs the following steps:
     * 1. Creates a Keycloak instance with the specified tenant configuration.
     * 2. Stores the tenant ID in local storage.
     * 3. Attempts to initialize Keycloak with the `login-required` option to force login if not authenticated.
     * 4. If the initial initialization fails, it retries with the `check-sso` option to check for an existing session.
     * 5. Updates the state with the new Keycloak instance and authentication status.
     * 6. Sets a timeout to periodically update the token.
     * 7. Redirects to the home page on failure.
     */
    var initKeycloak = function (tenantId) { return __awaiter(void 0, void 0, void 0, function () {
        var keycloakConfig, newKeycloak, keycloakInitOptions, isAuthenticatedResponse, initError_1, fallbackOptions, fallbackResponse, error_1;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    _a.trys.push([0, 6, , 7]);
                    keycloakConfig = {
                        realm: "tenant-".concat(tenantId),
                        clientId: globalSettings.ONLINE_VOTING_CLIENT_ID,
                        url: globalSettings.KEYCLOAK_URL,
                    };
                    newKeycloak = new Keycloak(keycloakConfig);
                    // Store the tenant ID for initialization
                    modifySelectedTenantId(tenantId);
                    navigate("/");
                    keycloakInitOptions = {
                        onLoad: "login-required",
                        checkLoginIframe: false,
                        // redirectUri: window.location.origin // + "/?tenant=" + tenantId
                    };
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 3, , 5]);
                    return [4 /*yield*/, newKeycloak.init(keycloakInitOptions)
                        // Update state with the new Keycloak instance
                    ];
                case 2:
                    isAuthenticatedResponse = _a.sent();
                    // Update state with the new Keycloak instance
                    setKeycloak(newKeycloak);
                    // User should be authenticated now due to login-required
                    localStorage.setItem("token", generateTokenStorage(newKeycloak));
                    setAuthenticated(true);
                    setIsKeycloakInitialized(true);
                    setTimeout(updateTokenPeriodically, 4e3);
                    navigate("/");
                    return [2 /*return*/, true];
                case 3:
                    initError_1 = _a.sent();
                    fallbackOptions = {
                        onLoad: "check-sso",
                        checkLoginIframe: false,
                    };
                    return [4 /*yield*/, newKeycloak.init(fallbackOptions)
                        // Update state with the new Keycloak instance
                    ];
                case 4:
                    fallbackResponse = _a.sent();
                    // Update state with the new Keycloak instance
                    setKeycloak(newKeycloak);
                    if (fallbackResponse) {
                        // User is authenticated
                        localStorage.setItem("token", generateTokenStorage(newKeycloak));
                        setAuthenticated(true);
                        setIsKeycloakInitialized(true);
                        setTimeout(updateTokenPeriodically, 4e3);
                        return [2 /*return*/, true];
                    }
                    else {
                        // User is not authenticated, but we have a Keycloak instance
                        setAuthenticated(false);
                        setIsKeycloakInitialized(true);
                        return [2 /*return*/, false];
                    }
                    return [3 /*break*/, 5];
                case 5: return [3 /*break*/, 7];
                case 6:
                    error_1 = _a.sent();
                    setAuthenticated(false);
                    navigate("/tenant"); // Redirect back on failure
                    return [2 /*return*/, false];
                case 7: return [2 /*return*/];
            }
        });
    }); };
    var fetchGetTenant = function () { return __awaiter(void 0, void 0, void 0, function () {
        return __generator(this, function (_a) {
            return [2 /*return*/, fetchGraphQL(operation, "GetTenant", {})];
        });
    }); };
    /**
     * Returns true only if the JWT has gold permissions and the JWT
     * authentication is fresh, i.e. performed less than 60 seconds ago.
     */
    // TODO: This is duplicated from jwt.rs in sequent-core, we should just use
    // the same WASM function if possible
    var isGoldUser = function () {
        var _a, _b, _c, _d;
        var acr = (_b = (_a = keycloak === null || keycloak === void 0 ? void 0 : keycloak.tokenParsed) === null || _a === void 0 ? void 0 : _a.acr) !== null && _b !== void 0 ? _b : null;
        var isGold = acr === IPermissions.GOLD_PERMISSION;
        var authTimeTimestamp = (_d = (_c = keycloak === null || keycloak === void 0 ? void 0 : keycloak.tokenParsed) === null || _c === void 0 ? void 0 : _c.auth_time) !== null && _d !== void 0 ? _d : 0;
        var authTime = new Date(authTimeTimestamp * 1000);
        var freshnessLimit = new Date(Date.now().valueOf() - 60 * 1000);
        var isFresh = authTime > freshnessLimit;
        return isGold && isFresh;
    };
    var reauthWithGold = function (redirectUri) { return __awaiter(void 0, void 0, void 0, function () {
        var error_2;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    _a.trys.push([0, 2, , 3]);
                    return [4 /*yield*/, (keycloak === null || keycloak === void 0 ? void 0 : keycloak.login({
                            acr: { essential: true, values: [IPermissions.GOLD_PERMISSION] },
                            redirectUri: redirectUri || window.location.href, // Use the passed URL or fallback to current URL
                        }))];
                case 1:
                    _a.sent();
                    return [3 /*break*/, 3];
                case 2:
                    error_2 = _a.sent();
                    console.error("Re-authentication failed:", error_2);
                    return [3 /*break*/, 3];
                case 3: return [2 /*return*/];
            }
        });
    }); };
    var createKeycloak = function (tenantId) {
        if (keycloak) {
            return;
        }
        /**
         * KeycloakConfig configures the connection to the Keycloak server.
         */
        var storedTenantId = tenantId || selectedTenantId || globalSettings.DEFAULT_TENANT_ID;
        if (location.pathname.endsWith("/tenant") && !selectedTenantId) {
            return;
        }
        modifySelectedTenantId(storedTenantId);
        var keycloakConfig = {
            realm: "tenant-".concat(storedTenantId),
            clientId: globalSettings.ONLINE_VOTING_CLIENT_ID,
            url: globalSettings.KEYCLOAK_URL,
        };
        var newKeycloak = new Keycloak(keycloakConfig);
        setKeycloak(newKeycloak);
    };
    var initializeKeycloak = function () { return __awaiter(void 0, void 0, void 0, function () {
        var keycloakInitOptions, isAuthenticatedResponse, error_3;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!keycloak) {
                        return [2 /*return*/];
                    }
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 3, , 4]);
                    keycloakInitOptions = {
                        // Configure that Keycloak will check if a user is already authenticated (when
                        // opening the app or reloading the page). If not authenticated, we'll handle
                        // this in the App component by showing the SelectTenant screen.
                        onLoad: location.pathname.endsWith("/tenant") ? "check-sso" : "login-required",
                        checkLoginIframe: false,
                        flow: "standard",
                        responseMode: "fragment", // Use fragment response mode
                    };
                    return [4 /*yield*/, keycloak.init(keycloakInitOptions)
                        // If the authentication was not successful, we'll let the App component handle it
                        // by showing the SelectTenant screen
                    ];
                case 2:
                    isAuthenticatedResponse = _a.sent();
                    // If the authentication was not successful, we'll let the App component handle it
                    // by showing the SelectTenant screen
                    if (!isAuthenticatedResponse) {
                        setAuthenticated(false);
                        setIsKeycloakInitialized(true); // Still mark as initialized so we can use it for login
                        localStorage.removeItem("token");
                        if (location.pathname.endsWith("/tenant") && selectedTenantId) {
                            modifySelectedTenantId(null);
                        }
                        return [2 /*return*/];
                    }
                    if (!keycloak.token) {
                        setAuthenticated(false);
                        setIsKeycloakInitialized(true); // Still mark as initialized so we can use it for login
                        return [2 /*return*/];
                    }
                    // If we get here the user is authenticated and we can update the state accordingly
                    localStorage.setItem("token", generateTokenStorage(keycloak));
                    setAuthenticated(true);
                    setTimeout(updateTokenPeriodically, 4e3);
                    setIsKeycloakInitialized(true);
                    return [3 /*break*/, 4];
                case 3:
                    error_3 = _a.sent();
                    setAuthenticated(false);
                    setIsKeycloakInitialized(true); // Still mark as initialized so we can use it for login
                    localStorage.removeItem("token");
                    if (location.pathname.endsWith("/tenant") && selectedTenantId) {
                        modifySelectedTenantId(null);
                    }
                    return [3 /*break*/, 4];
                case 4: return [2 /*return*/];
            }
        });
    }); };
    useEffect(function () {
        if (loadedGlobalSettings && !keycloak) {
            createKeycloak();
        }
    }, [loadedGlobalSettings, keycloak]);
    useEffect(function () {
        if (!keycloak || isKeycloakInitialized) {
            return;
        }
        initializeKeycloak();
    }, [isKeycloakInitialized, keycloak]);
    var updateTokenPeriodically = function () { return __awaiter(void 0, void 0, void 0, function () {
        var refreshed;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!keycloak) return [3 /*break*/, 2];
                    return [4 /*yield*/, keycloak.updateToken(sleepSecs + bufferSecs)];
                case 1:
                    refreshed = _a.sent();
                    if (!keycloak.token) {
                        return [2 /*return*/];
                    }
                    if (refreshed) {
                        localStorage.setItem("token", generateTokenStorage(keycloak));
                    }
                    _a.label = 2;
                case 2: return [4 /*yield*/, sleep(sleepSecs * 1e3)];
                case 3:
                    _a.sent();
                    updateTokenPeriodically();
                    return [2 /*return*/];
            }
        });
    }); };
    var extractPermissionLabels = function (input) {
        var regex = /\"(.*?)\"/g;
        var matches = [];
        var match;
        while ((match = regex.exec(input)) !== null) {
            matches.push(match[1]);
        }
        return matches;
    };
    // This effect loads the users profile in order to extract the username
    useEffect(function () {
        /**
         * Load the profile for of the user from Keycloak
         */
        function loadProfile() {
            var _a, _b, _c, _d, _e;
            return __awaiter(this, void 0, void 0, function () {
                var profile, newTenantId, tokenPermissionLabels, permissionLabelsArray, _f;
                return __generator(this, function (_g) {
                    switch (_g.label) {
                        case 0:
                            if (!keycloak) {
                                return [2 /*return*/];
                            }
                            _g.label = 1;
                        case 1:
                            _g.trys.push([1, 3, , 4]);
                            return [4 /*yield*/, keycloak.loadUserProfile()];
                        case 2:
                            profile = _g.sent();
                            if (profile.id) {
                                setUserId(profile.id);
                            }
                            if (profile.email) {
                                setEmail(profile.email);
                            }
                            if (profile.firstName) {
                                setFirstName(profile.firstName);
                            }
                            if (profile.username) {
                                setUsername(profile.username);
                            }
                            newTenantId = (_a = profile === null || profile === void 0 ? void 0 : profile.attributes["tenant-id"]) === null || _a === void 0 ? void 0 : _a[0];
                            if (newTenantId) {
                                setTenantId(newTenantId);
                            }
                            if ((_b = keycloak.tokenParsed) === null || _b === void 0 ? void 0 : _b.trustee) {
                                setTrustee((_c = keycloak.tokenParsed) === null || _c === void 0 ? void 0 : _c.trustee);
                            }
                            tokenPermissionLabels = (_e = (_d = keycloak.tokenParsed) === null || _d === void 0 ? void 0 : _d["https://hasura.io/jwt/claims"]) === null || _e === void 0 ? void 0 : _e["x-hasura-permission-labels"];
                            if (tokenPermissionLabels) {
                                permissionLabelsArray = extractPermissionLabels(tokenPermissionLabels);
                                setPermissionLabels(permissionLabelsArray);
                            }
                            return [3 /*break*/, 4];
                        case 3:
                            _f = _g.sent();
                            console.log("error trying to load the users profile");
                            return [3 /*break*/, 4];
                        case 4: return [2 /*return*/];
                    }
                });
            });
        }
        // Only load the profile if a user is authenticated
        if (isAuthenticated) {
            loadProfile();
        }
    }, [isAuthenticated, keycloak]);
    /**
     * Initiate the logout
     */
    var logout = function (redirectUri) {
        if (!keycloak) {
            return;
        }
        localStorage.removeItem("token");
        sessionStorage.removeItem("selected-election-event-tally-id");
        var redirect = redirectUri ? redirectUri : window.location.origin;
        // Redirect to the main route after logout
        keycloak.logout({
            redirectUri: redirect,
        });
    };
    /**
     * Check if the user has the given role
     * @param role to be checked
     * @returns whether or not if the user has the role
     */
    var hasRole = function (role) {
        var _a, _b;
        if (!keycloak) {
            return false;
        }
        return !!((_b = (_a = keycloak.tokenParsed) === null || _a === void 0 ? void 0 : _a["https://hasura.io/jwt/claims"]) === null || _b === void 0 ? void 0 : _b["x-hasura-allowed-roles"].includes(role));
    };
    var getAccessToken = function () { return keycloak === null || keycloak === void 0 ? void 0 : keycloak.token; };
    var isAuthorized = function (checkSuperAdmin, someTenantId, role) {
        var isSuperAdmin = globalSettings.DEFAULT_TENANT_ID === tenantId;
        var isValidTenant = tenantId === someTenantId;
        if (!((checkSuperAdmin && isSuperAdmin) || (!isNull(someTenantId) && isValidTenant))) {
            return false;
        }
        var roleList = isString(role) ? [role] : role;
        return roleList.find(function (roleItem) { return hasRole(roleItem); }) != undefined;
    };
    var openProfileLink = function () { return __awaiter(void 0, void 0, void 0, function () {
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!keycloak) {
                        return [2 /*return*/];
                    }
                    return [4 /*yield*/, keycloak.accountManagement()];
                case 1:
                    _a.sent();
                    return [2 /*return*/];
            }
        });
    }); };
    var updateTokenAndPermissionLabels = function () {
        var _a;
        if (keycloak) {
            var refresh = keycloak.updateToken(((_a = keycloak.tokenParsed) === null || _a === void 0 ? void 0 : _a.exp) || 30);
            refresh
                .then(function () {
                var _a, _b;
                var tokenPermissionLabels = (_b = (_a = keycloak.tokenParsed) === null || _a === void 0 ? void 0 : _a["https://hasura.io/jwt/claims"]) === null || _b === void 0 ? void 0 : _b["x-hasura-permission-labels"];
                if (tokenPermissionLabels) {
                    var permissionLabelsArray = extractPermissionLabels(tokenPermissionLabels);
                    setPermissionLabels(permissionLabelsArray);
                }
            })
                .catch(function (error) {
                console.log("error updating token", error);
            });
        }
    };
    // Setup the context provider
    return (_jsxs(AuthContext.Provider, __assign({ value: {
            isAuthenticated: isAuthenticated,
            userId: userId,
            username: username,
            email: email,
            firstName: firstName,
            tenantId: tenantId,
            trustee: trustee,
            logout: logout,
            hasRole: hasRole,
            getAccessToken: getAccessToken,
            isAuthorized: isAuthorized,
            openProfileLink: openProfileLink,
            permissionLabels: permissionLabels,
            updateTokenAndPermissionLabels: updateTokenAndPermissionLabels,
            isGoldUser: isGoldUser,
            reauthWithGold: reauthWithGold,
            initKeycloak: initKeycloak,
        } }, { children: [selectedTenantId ? props.children : _jsx(SelectTenant, {}), _jsx(Dialog, __assign({ variant: "info", hasCloseButton: false, open: openModal, ok: t("common.label.logout"), cancel: t("common.label.continue"), title: t("common.label.warning"), handleClose: function (result) {
                    if (result) {
                        modifySelectedTenantId(null);
                        logout(window.location.origin + "/tenant");
                    }
                    else {
                        setOpenModal(false);
                    }
                } }, { children: t("common.message.continueOrLogout") }))] })));
};
export default AuthContextProvider;
